import { Card } from '@startuptools/ui/joy/Card';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Trans, useLingui } from '@lingui/react';
import { Checkbox } from '@startuptools/ui/joy/Checkbox';
import { GqlRegistrationTodoItemKind, GqlUpdateRegistrationTodoItem } from '../../../graphql/base-types.graphql';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { useToggleRegistrationTodoMutation } from './registration-todo-list.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { DocumentNode } from 'graphql';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import React from 'react';
import { Link } from '@startuptools/ui/joy/Link';
import { GqlRegistrationTodoCompletion } from '../../../graphql/react-operations';
import { AlertDialog } from '../../../components/simple-dialogs/AlertDialog';

const TodoItem = ({
  title,
  description,
  checked = false,
  onToggle,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  checked: boolean;
  onToggle: (checked: boolean) => void;
}) => {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <FormControl sx={{ flexDirection: 'row', gap: 2 }}>
        <Checkbox checked={checked} onChange={event => onToggle(event.target.checked)} sx={{ mt: 0.5 }} />
        <FormLabel sx={{ cursor: 'pointer' }}>
          <Stack spacing={0.5}>
            <Typography
              level="title-md"
              sx={{
                textDecoration: checked ? 'line-through' : 'none',
                color: checked ? 'neutral.400' : 'inherit',
              }}
            >
              {title}
            </Typography>
            <Typography
              level="body-sm"
              sx={{
                fontWeight: 400,
                textDecoration: checked ? 'line-through' : 'none',
                color: checked ? 'neutral.400' : 'neutral',
              }}
            >
              {description}
            </Typography>
          </Stack>
        </FormLabel>
      </FormControl>
    </Stack>
  );
};

export const RegistrationToDoList = ({
  todos,
  completions,
  shareRegistrationId,
  optionsRegistrationId,
  optionsSharesRegistrationId,
  refetchQueries,
}: {
  todos: GqlRegistrationTodoItemKind[];
  completions: GqlRegistrationTodoCompletion[];
  refetchQueries: DocumentNode[];
} & Pick<
  GqlUpdateRegistrationTodoItem,
  'optionsRegistrationId' | 'optionsSharesRegistrationId' | 'shareRegistrationId'
>) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  // eslint-disable-next-line sut/loading-mutation
  const [doToggle] = useToggleRegistrationTodoMutation({ refetchQueries });
  const initiallyUncompleted = React.useRef(completions.length < todos.length);
  const [showCelebration, setShowCelebration] = React.useState(false);

  React.useEffect(() => {
    if (initiallyUncompleted.current && completions.length === todos.length) {
      setShowCelebration(true);
    }
  }, [completions.length, todos.length]);

  const toggleCompletion = async (kind: GqlRegistrationTodoItemKind, completed: boolean) => {
    try {
      await doToggle({
        variables: {
          input: { kind, shareRegistrationId, optionsRegistrationId, optionsSharesRegistrationId, completed },
          companyId,
        },
      });
    } catch {
      genericErrorToast();
    }
  };
  const todoProps = React.useMemo<
    Record<GqlRegistrationTodoItemKind, { title: React.ReactNode; description: React.ReactNode }>
  >(() => {
    return {
      [GqlRegistrationTodoItemKind.BankNote]: {
        title: <Trans id="Request bank note" />,
        description: (
          <Trans id="Request a physical bank note from the bank that shows the exact amount of money that has been paid into the account. Note that the amount should match the amount in the share issue registration form exactly, and the document should be signed and stamped by the bank." />
        ),
      },
      [GqlRegistrationTodoItemKind.PrintMeetingProtocol]: {
        title: <Trans id="Print the meeting protocol" />,
        description: (
          <Trans
            id="Print the meeting protocol and verify it. Read more about how to do this <link>here</link>."
            components={{
              link: (
                <Link
                  href={
                    i18n.locale === 'sv'
                      ? 'https://startuptools.org/se/sa-vidimerar-du-ett-protokoll'
                      : 'https://startuptools.org/se/eng/how-to-verify-meeting-minutes-vidimera'
                  }
                />
              ),
            }}
          />
        ),
      },
      [GqlRegistrationTodoItemKind.PrintRegistrationForm]: {
        title: <Trans id="Print the registration form" />,
        description: <Trans id="Print the registration form and sign it with blue ink." />,
      },
      [GqlRegistrationTodoItemKind.SendToBolagsverket]: {
        title: <Trans id="Send the documents to Bolagsverket" />,
        description: (
          <Trans id="Put all printed documents in an envelope and send it to Bolagsverket, 851 81 Sundsvall." />
        ),
      },
      [GqlRegistrationTodoItemKind.UpdateShareRegister]: {
        title: <Trans id="Update the share register" />,
        description: (
          <Trans id="You can do this before the share issue is registered, and connect Bolagsverket's case number later." />
        ),
      },
    };
  }, [i18n.locale]);

  return (
    <Card>
      <Stack gap={2}>
        <Typography level="h4">
          <Trans id="What is left to do?" />
        </Typography>
        <Stack gap={3}>
          {todos.map(item => (
            <TodoItem
              key={item}
              {...todoProps[item]}
              onToggle={completed => toggleCompletion(item, completed)}
              checked={completions.some(c => c.kind === item)}
            />
          ))}
        </Stack>

        {showCelebration && (
          <AlertDialog
            title={<div className="text-4xl">🎉</div>}
            acceptButtonText={<Trans id="Close" />}
            onAccept={() => setShowCelebration(false)}
            maxWidth="sm"
          >
            <Typography level="body-sm">
              <Trans id="You've done all steps in the registration process, and you can now wait for Bolagsverket to process it. You should be proud of yourself!" />
            </Typography>
          </AlertDialog>
        )}
      </Stack>
    </Card>
  );
};
